<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="isNew">{{ $t('add_product.new_product') }}</h1>
      <h1 v-else>{{ $t('add_product.edit_product') }}</h1>
    </div>
    <div class="details-component__content content">

      <form class="form fixed-form">

        <table class="details-table tenant no-col-padding edit-table fixed-table label-col-wide-260">
          <tr v-if="isSuperAdmin()" class="details-table__row"
            :class="{ 'details-table__row_error': errors.has('landlord') }">
            <td class="details-table__col details-table__col_label">
              <label for="landlord" class="form__label">{{ $t('common.landlord') }}</label>
            </td>
            <td class="details-table__col landlord__name" v-if="form.id === undefined" colspan="2">
              <el-select name="landlord" v-model="landlord" v-validate="'validLandlord'"
                :data-vv-as="$t('common.landlord')" v-if="landlords.items" :placeholder="$t('common.landlord')"
                @change="getZonesByLandlord(true)">
                <el-option v-for="landlord in landlords.items" :key="landlord.id" :label="landlord.name"
                  :value="landlord.id">
                </el-option>
              </el-select>
              <span v-else-if="landlord">{{ landlord.name }}</span>
              <tooltip v-show="errors.has('landlord')" class="form__validation">{{ $t('errors.landlord_field_required')
                }}</tooltip>
            </td>
            <td class="details-table__col landlord__name disabled" v-if="form.id !== undefined && form.partner"
              colspan="2">
              {{ form.partner.name }}
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('name') }">
            <td class="details-table__col details-table__col_label">
              <label for="name" class="form__label">{{ $t('common.name') }}</label>
            </td>
            <td class="details-table__col pack__name" colspan="2">
              <input type="text" class="form__input" name="name" :placeholder="$t('common.name')" id="name" value=""
                v-model="form.name" v-validate data-vv-rules="required|min:3|max:255" data-vv-as="name"
                data-vv-delay="1000" />
              <tooltip v-show="errors.has('name:required')" class="form__validation">{{ $t('errors.field_required') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row auto-height with-border"
            :class="{ 'details-table__row_error': errors.has('zone') }">
            <td class="details-table__col details-table__col_label">
              <label for="permit_count" class="form__label">{{ $tc('common.zones', zones.length) }}</label>
            </td>
            <td class="details-table__col txt-to-left" colspan="2">
              <el-select v-model="checkedZones" v-validate class="fit-to-parent" multiple filterable
                data-vv-rules="required" data-vv-as="checkedZones" name="zone"
                :placeholder="$tc('common.zones', zones.length)">
                <el-option v-for="zone in getZonesByLandlord()" :key="zone.id" :label="`${zone.code}`" :value="zone.id">
                </el-option>
              </el-select>
              <tooltip v-show="errors.has('zone')" class="form__validation">{{ $t('errors.zone_required') }}</tooltip>
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('productType') }"
            v-show="isNew">
            <td class="details-table__col details-table__col_label">
              <label for="productType" class="form__label">{{ $tc('add_permit.product', 'product.total') }}</label>
            </td>
            <td class="details-table__col form__field_radiobuttons txt-to-left" colspan="2">
              <el-select name="productType" v-model="type">
                <el-option v-for="productType in productTypes" :key="productType" :label="$t('product.' + productType)"
                  :value="productType" v-if="productType !== 'easyParking' || isSuperAdmin()">
                </el-option>
              </el-select>
              <tooltip v-show="errors.has('productType')" class="form__validation">{{ $t('errors.product_type_required')
                }}</tooltip>
            </td>
          </tr>

          <tr v-show="isGuestParking()" class="details-table__row">
            <td class="details-table__col details-table__col_label">
              <label for="bookable_parking_spots_count" class="form__label">{{
        $t('products.bookable_parking_spots_count') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" name="bookable_parking_spots_count"
                id="bookable_parking_spots_count" value="" v-validate data-vv-rules="numeric|min:1"
                data-vv-as="bookable_parking_spots_count" v-model="form.bookable_parking_spots_count" />
              <tooltip v-show="errors.has('bookable_parking_spots_count')" class="form__validation">
                {{ $t('errors.field_numeric') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('conference_url') }"
            v-show="isConferenceParking()">
            <td class="details-table__col details-table__col_label">
              <label for="conference_url" class="form__label">{{ $t('conference.conference_url') }}</label>
              {{ conferenceBaseUrl }}
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" name="conference_url"
                :placeholder="$t('conference.conference_url')" id="conference_url" ref="conference_url" value=""
                v-model="form.conferenceUrl" v-validate data-vv-rules="required|validUrl|uniqueUrl"
                data-vv-as="conference_url" data-vv-delay="1000" />
              <tooltip v-show="errors.has('conference_url:required')" class="form__validation">{{
        $t('errors.field_required') }}</tooltip>
              <tooltip v-show="errors.has('conference_url:validUrl')" class="form__validation">{{
        $t('conference.error_url_invalid') }}</tooltip>
              <tooltip v-show="errors.has('conference_url:uniqueUrl')" class="form__validation">{{
        $t('conference.error_url_not_unique') }}</tooltip>
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('permit_count') }"
            v-if="isPermitPack() || isConferenceParking()">
            <td class="details-table__col details-table__col_label">
              <label for="permit_count" class="form__label">{{ $t('add_product.parking_spots') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" name="permit_count" :placeholder="$t('add_product.parking_spots')"
                id="permit_count" value="" v-model="form.total" v-validate
                data-vv-rules="required|numeric|min:1|cannotBeLower" data-vv-as="$t('add_product.parking_spots')"
                data-vv-delay="1000" />
              <tooltip v-show="errors.has('permit_count:numeric')" class="form__validation">{{
        $t('errors.field_numeric') }}</tooltip>
              <tooltip v-show="errors.has('permit_count:required')" class="form__validation">{{
        $t('errors.field_required') }} {{ form.total }}</tooltip>
              <tooltip v-show="errors.has('permit_count:cannotBeLower')" class="form__validation">{{
        $t('errors.permit_count_too_low') }} {{ form.issued }}</tooltip>
            </td>
          </tr>

          <tr class="details-table__row" v-if="isPermitPack() && isLandlord()">
            <td class="details-table__col details-table__col_label">
              <label for="permit_count" class="form__label">{{ $t('products.parking_spot_number') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" name="parking_spot_number" id="parking_spot_number" value=""
                v-model="form.permanentSpotNumber" />
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('parking_price') }"
            v-if="isPermitPack() && isLandlord()">
            <td class="details-table__col details-table__col_label">
              <label for="parking_price" class="form__label">{{ $t('add_product.parking_price') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" name="parking_price" :placeholder="$t('add_product.parking_price')"
                id="parking_price" value="" v-model="form.price" v-validate data-vv-rules="decimal:2"
                data-vv-as="$t('add_product.parking_price')" data-vv-delay="1000" />
              <tooltip v-show="errors.has('parking_price')" class="form__validation">{{ $t('errors.field_numeric') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('conference_price') }"
            v-if="isConferenceParking()">
            <td class="details-table__col details-table__col_label">
              <label for="conference_price" class="form__label">{{ $t('conference.price_per_vehicle') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" name="conference_price"
                :placeholder="$t('conference.price_per_vehicle')" id="conference_price" value=""
                v-model="conferencePricePerVehicle" v-validate data-vv-rules="decimal:2"
                data-vv-as="$t('conference.price_per_vehicle')" data-vv-delay="1000" />
              <tooltip v-show="errors.has('conference_price')" class="form__validation">{{ $t('errors.field_numeric') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row" v-if="isLandlord()">
            <td class="details-table__col details-table__col_label">
              <label for="comment" class="form__label">{{ $t('parking.comment') }}</label>
            </td>
            <td class="details-table__col">
              <input type="text" class="form__input" name="comment" id="comment" v-model="form.comment" />
            </td>
          </tr>

          <tr class="details-table__row" v-if="isLandlord() && user.paymentModuleEnabled">
            <td class="details-table__col details-table__col_label">
              <label for="comment" class="form__label">{{ $t('parking.cost_center') }}</label>
            </td>
            <td class="details-table__col">
              <input type="text" class="form__input" name="cost_center" id="cost_center" v-model="form.cost_center" />
            </td>
          </tr>

          <tr class="details-table__row pack-commission"
            :class="{ 'details-table__row_error': errors.has('commission_value') || errors.has('commission_type') }"
            v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col details-table__col_label">
              <label for="commission" class="form__label">{{ $t('products.commission') }}</label>
            </td>
            <td class="details-table__col">
              <div>
                <input type="text" class="form__input" name="commission_value" :placeholder="$t('products.commission')"
                  id="commission_value" value="" v-model="form.commission_value" v-validate data-vv-rules="decimal:2"
                  data-vv-as="$t('products.commission')" data-vv-delay="1000" />
              </div>
              <tooltip v-show="errors.has('commission_value')" class="form__validation">{{ $t('errors.field_numeric') }}
              </tooltip>
            </td>
            <td class="details-table__col">
              <el-select class="short" name="commission_type" v-model="commissionType" placeholder=""
                v-validate="`${form.commission_value ? 'required' : ''}`">
                <el-option value="" label=""></el-option>
                <el-option value="%" label="%"></el-option>
                <el-option value="€" label="€"></el-option>
              </el-select>
              <tooltip v-show="errors.has('commission_type')" class="form__validation">{{
        $t('errors.commission_type_required') }}</tooltip>
            </td>


          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('perDay:numeric') }"
            v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col details-table__col_label">
              <label for="perDay" class="form__label">{{ $t('products.per_day') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" :placeholder="$t('products.per_day')" name="perDay" id="perDay"
                value="" v-model="form.perDay" v-validate data-vv-rules="numeric|min:1" data-vv-as="perDay"
                data-vv-delay="1000" />
              <tooltip v-show="errors.has('perDay:numeric')" class="form__validation">{{ $t('errors.field_numeric') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('perDay:numeric') }"
            v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col details-table__col_label">
              <label for="concurrentParkings" class="form__label">{{ $t('products.concurrent_parkings') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" name="concurrentParkings"
                :placeholder="$t('products.concurrent_parkings')" id="concurrentParkings" value=""
                v-model="form.concurrentParkings" v-validate data-vv-rules="numeric|min:1"
                data-vv-as="concurrentParkings" data-vv-delay="1000" />
              <tooltip v-show="errors.has('concurrentParkings:numeric')" class="form__validation">{{
        $t('errors.field_numeric') }}</tooltip>
            </td>
          </tr>

          <tr class="details-table__row" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col details-table__col_label">
              <label for="frequency" class="form__label no-hide">{{ $t('products.frequency') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <el-select name="frequency" v-model="frequency" placeholder="">
                <el-option v-for="step of steps" :label="step.value" :value="step.key" :key="step.key">
                </el-option>
              </el-select>
            </td>
          </tr>

          <tr v-if="canDateExpiration" class="details-table__row form__field_datepicker">
            <td class="details-table__col details-table__col_label">
              <label class="form__label">{{ $t('products.expiration_date') }}</label>
            </td>
            <td class="details-table__col auto-height">
              <el-date-picker v-model="form.dateExpiration" format="dd.MM.yyyy" type="date" name="dateExpiration"
                :picker-options="expireTimeOptions" :clearable="true" value-format="dd.MM.yyyy">
              </el-date-picker>
            </td>
          </tr>

          <tr class="details-table__row" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col details-table__col_label">
              <label for="parking_price" class="form__label">{{ $t('add_product.time_limit') }}</label>
            </td>
            <td class="" colspan="3">
              <div class="form__field">
                <InputBoxes v-model="LimitMonthly" inputFrom="right" treatAs="datetimeinput" :maxDays="999">
                </InputBoxes>
              </div>
            </td>
          </tr>
          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('perDay:numeric') }"
              v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col details-table__col_label">
              <label for="paymentGracePeriod" class="form__label">{{ $t('products.payment_grace_period') }}</label>
            </td>
            <td class="details-table__col" colspan="2">
              <input type="text" class="form__input" :placeholder="$t('products.payment_grace_period')" name="paymentGracePeriod" id="paymentGracePeriod"
                     value="" v-model="form.payment_grace_period" v-validate data-vv-rules="numeric|min:1" data-vv-as="perDay"
                     data-vv-delay="1000" />
              <tooltip v-show="errors.has('perDay:numeric')" class="form__validation">{{ $t('errors.field_numeric') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="!isGuestParking() || !isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="can_expiration_date" name="can_expiration_date"
                  class="form__fieldset__checkbox" v-model="canDateExpiration">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.enable_expiration_date') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isPermitPack()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="can_exceed_capacity" name="can_exceed_capacity"
                  class="form__fieldset__checkbox" v-model="form.canExceedCapacity">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.enable_virtual_parking_spots') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isPermitPack()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="can_use_cards" name="can_use_cards" class="form__fieldset__checkbox"
                  v-model="form.canUseCards">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_to_use_cards') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isPermitPack()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_permit_start" name="tenant_can_manage_permit_start"
                  class="form__fieldset__checkbox" v-model="form.tenantCanManagePermitStart">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_tenant_to_manage_permit_start') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isPermitPack()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_permit_expiration"
                  name="tenant_can_manage_permit_expiration" class="form__fieldset__checkbox"
                  v-model="form.tenantCanManagePermitExpiration">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_tenant_to_manage_permit_expiration')
                  }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isPermitPack()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_spot" name="tenant_can_manage_spot"
                  class="form__fieldset__checkbox" v-model="form.tenantCanManagePermitSpot">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_tenant_to_manage_spot') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isPermitPack()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="auto_start_enabled" name="auto_start_enabled"
                  class="form__fieldset__checkbox" v-model="form.auto_start_enabled">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.auto_start_enabled') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="shared_product_session" name="shared_product_session"
                  class="form__fieldset__checkbox" v-model="form.isUseSharedSession">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.shared_product_session') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="is_time_limit_edit" name="is_time_limit_edit"
                  class="form__fieldset__checkbox" v-model="form.is_time_limit_edit">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('parking.time_limit_edite') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="is_required_comment" name="is_required_comment"
                  class="form__fieldset__checkbox" v-model="form.is_required_comment"
                  :disabled="form.is_allow_guest_parking">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.is_required_comment') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="is_allow_guest_parking" name="is_allow_guest_parking"
                  class="form__fieldset__checkbox" v-model="form.is_allow_guest_parking"
                  :disabled="form.is_required_comment">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.is_allow_guest_parking') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="payment_required" name="payment_required" class="form__fieldset__checkbox"
                  v-model="form.payment_required" :disabled="!user.paymentModuleEnabled">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.is_paid_guest_parking') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="allow_end_time_override" name="allow_end_time_override"
                  class="form__fieldset__checkbox" v-model="form.allow_end_time_override">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_end_time_override') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking() || isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="allow_past_start" name="allow_past_start"
                  class="form__fieldset__checkbox" v-model="form.allow_past_start">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.allow_past_start') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isGuestParking() && user.autopayEnabled">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="autopay_enabled" name="autopay_enabled"
                  class="form__fieldset__checkbox" v-model="form.autopay_enabled">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('autopay.autopay_enabled') }}</span>
              </label>
            </td>
          </tr>

          <tr class="details-table__row auto-height" v-if="isEasyParking()">
            <td class="details-table__col txt-to-left padding-t-4" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="requires_confirmation" name="requires_confirmation"
                  class="form__fieldset__checkbox" v-model="form.requires_confirmation">
                <span class="form__fieldset__custom-checkbox"></span>
                <span class="custom-checkbox-label">{{ $t('add_product.confirm_all_sessions') }}</span>
              </label>
            </td>
          </tr>

        </table>

        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'products' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'product', params: { id: $route.params.id }}"
            class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="onSubmit()">
            {{ $t('common.save') }}</button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import 'styles/blocks/details.scss';
import InputBoxes from 'common/InputBoxes';

export default {
  components: {
    Tooltip,
    InputBoxes,
  },
  watch: {
    $route: 'fetchAllData',
    'form.name': 'updateConferenceUrl',

    LimitMonthly(minutes) {
      this.form.time_limit = minutes * 60;
    },
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    selectedLandlord() {
      if (this.form.id !== undefined && this.form.partner) {
        return this.form.partner.id;
      }

      return this.landlord;
    },

    ...mapGetters({
      allConferenceUrls: 'products/product/conferenceUrls',
      landlords: 'landlord/landlords/landlords',
      zones: 'products/zones/zones',
      product: 'products/product/product',
      productsLimit: 'landlord/products/limit',
      productsKeywordFilter: 'landlord/products/keywordFilter',
      productError: 'products/product/error',
      user: 'user/user/user',
    }),
  },
  mounted() {
    this.fetchAllData();
  },
  created() {
    this.resetValidators();
    this.createValidators();
  },
  data() {
    return {
      LimitMonthly: null,
      form: {},
      canDateExpiration: false,
      landlord: null,
      frequency: 0,
      commissionType: null,
      type: 'permitPack',
      conferenceBaseUrl: `${process.env.BASE_URL}conference/`,
      conferencePricePerVehicle: 5,
      checkedZones: [],
      steps: [
        { key: -1440, value: `-24 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -720, value: `-12 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -480, value: `-8 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -360, value: `-6 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -300, value: `-5 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -240, value: `-4 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -180, value: `-3 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -120, value: `-2 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -90, value: `-1 ${this.$t('pricingrules.hour')} 30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -60, value: `-1 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: -45, value: `-45 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -30, value: `-30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -15, value: `-15 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -10, value: `-10 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: -5, value: `-5 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 0, value: '' },
        { key: 5, value: `5 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 10, value: `10 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 15, value: `15 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 30, value: `30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 45, value: `45 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 60, value: `1 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 90, value: `1 ${this.$t('pricingrules.hour')} 30 ${this.$t('pricingrules.minute')}`, unit: 'minute' },
        { key: 120, value: `2 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 180, value: `3 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 240, value: `4 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 300, value: `5 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 360, value: `6 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 480, value: `8 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 720, value: `12 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
        { key: 1440, value: `24 ${this.$t('pricingrules.hour')}`, unit: 'minute' },
      ],
      productTypes: ['permitPack', 'guestParking', 'conferenceParking', 'easyParking'],
      expireTimeOptions: {
        firstDayOfWeek: 1,
      },
    };
  },
  methods: {
    changeTimeFormat(hours) {
      this.form.time_limit = hours * 60 * 60;
    },
    fetchAllData() {
      this.fetchZones();
      if (this.$route.params.id) {
        this.fetchProduct();
      } else {
        this.resetValidators();
        this.$store.commit(`products/product/${mTypes.CLEAR_STATE}`);
        this.canDateExpiration = false;
        this.form = {};
        this.frequency = 0;
        this.commissionType = null;
        this.type = 'permitPack';
        this.checkedZones = [];
        this.conferencePricePerVehicle = 5;
      }
      if (this.isSuperAdmin()) {
        this.fetchLandlords();
        this.isEasyParking();
      }
      this.fetchConferenceUrls();
    },
    resetValidators() {
      this.landlord = this.user.data.selectedRole.id;
      this.$validator.remove('cannotBeLower');
      this.$validator.extend('cannotBeLower', {
        getMessage: () => 'Number cannot be lower then assigned permits',
        validate: (value) => {
          if (!this.form.canExceedCapacity && this.product.issued) {
            return value >= this.product.issued;
          }
          return true;
        },
      });
      this.$validator.remove('validUrl');
      this.$validator.extend('validUrl', {
        getMessage: () => 'The url must only contain letters from a-z, numbers and -',
        validate: (value) => {
          const sanitized = this.sanitizeConferenceUrl(value);
          return (sanitized === value);
        },
      });
      this.$validator.remove('uniqueUrl');
      this.$validator.extend('uniqueUrl', {
        getMessage: () => 'The entered url is not unique',
        validate: value => !this.isConferenceParking() || !this.allConferenceUrls.includes(value),
      });
      this.$validator.clean();
    },
    fetchLandlords() {
      if (this.user.data) {
        this.$store.dispatch('landlord/landlords/list', { user: this.user });
      }
    },
    fetchZones() {
      this.$store.dispatch('products/zones/fetch', { user: this.user });
    },
    fetchProduct() {
      this.$store.dispatch('products/product/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
        this.form = this.product;
        console.log(this.form);
        this.LimitMonthly = this.product.time_limit / 60;
        this.form.dateExpiration = this.product.dateExpiration ? moment(this.product.dateExpiration).format('DD.MM.YYYY') : null;
        this.canDateExpiration = !!this.form.dateExpiration;
        this.type = this.product.type;
        if (this.product.commission) {
          this.form.commission_value = this.product.commission.value;
          this.commissionType = this.product.commission.type;
        }
        if (this.product.frequency) {
          this.frequency = this.product.frequency;
        }
        if (this.type === 'conferenceParking') {
          this.conferencePricePerVehicle = this.form.price;
        }
        this.product.zones.forEach((el) => {
          this.checkedZones.push(el.id);
        });
      });
    },
    fetchConferenceUrls() {
      if (this.isNew) {
        this.$store.dispatch('products/product/conferenceUrls', { id: -1, user: this.user });
      } else {
        this.$store.dispatch('products/product/conferenceUrls', { id: this.$route.params.id, user: this.user });
      }
    },
    onSubmit() {
      this.$store.commit(`products/product/${mTypes.DATA_ERROR}`, null);
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.form.type = this.type;
          this.form.zones = [];
          this.checkedZones.forEach((el) => {
            this.form.zones.push({ id: el });
          });
          if (!this.form.canExceedCapacity) {
            this.form.canExceedCapacity = false;
          }
          if (!this.form.canUseCards) {
            this.form.canUseCards = false;
          }
          if (!this.form.tenantCanManagePermitStart) {
            this.form.tenantCanManagePermitStart = false;
          }
          if (!this.form.tenantCanManagePermitExpiration) {
            this.form.tenantCanManagePermitExpiration = false;
          }
          if (!this.form.tenantCanManagePermitSpot) {
            this.form.tenantCanManagePermitSpot = false;
          }
          if (!this.form.isUseSharedSession) {
            this.form.isUseSharedSession = false;
          }
          if (!this.form.allow_past_start) {
            this.form.allow_past_start = false;
          }
          if (!this.form.auto_start_enabled) {
            this.form.auto_start_enabled = false;
          }
          if (!this.form.total) {
            this.form.total = 0;
          }
          if (!this.form.perDay) {
            this.form.perDay = null;
          }
          if (this.frequency || this.frequency === 0) {
            this.form.frequency = this.frequency;
          }
          if (this.form.frequency === 0) {
            this.form.frequency = null;
          }
          if (this.form.bookable_parking_spots_count) {
            this.form.bookable_parking_spots_count = Number(this.form.bookable_parking_spots_count)
          }
          if (this.form.commission_value && this.form.commission_value !== '') {
            this.form.commission = {};
            this.form.commission.value = this.form.commission_value;
            this.form.commission.type = this.commissionType;
          } else {
            this.form.commission = null;
          }
          if (!this.isConferenceParking()) {
            this.form.conferenceUrl = null;
          } else {
            this.form.price = this.conferencePricePerVehicle;
          }
          if (!this.canDateExpiration || !this.form.dateExpiration) {
            this.form.dateExpiration = null;
          } else {
            this.form.dateExpiration = moment(this.form.dateExpiration, 'DD.MM.YYYY').format();
          }
          this.form.partner_id = this.isLandlord() ? this.landlord : this.user.data.selectedRole.id;
          this.$store.dispatch('products/product/saveProduct', { product: this.form, user: this.user }).then(() => {
            if (!this.productError) {
              this.$store.dispatch('landlord/products/fetch', { user: this.user, limit: this.productsLimit, keyword: this.productsKeywordFilter });
              if (!this.form.id && this.isGuestParking() || this.isEasyParking()) {
                this.$store.dispatch('pricingrules/pricingrule/save', {
                  user: this.user,
                  rule: {
                    productId: this.product.id,
                    endDate: null,
                    endMinutes: 1439,
                    maxMinutes: 1440,
                    maxTime: '',
                    startDate: null,
                    startMinutes: 0,
                    startingPrice: 0,
                    steps: [],
                    weekdays: [0, 1, 2, 3, 4, 5, 6],
                  },
                }).then(() => {
                  this.form = {};
                  this.$router.push({ name: 'product', params: { id: this.product.id } });
                });
              } else {
                this.form = {};
                this.$router.push({ name: 'product', params: { id: this.product.id } });
              }
            }
          })
            .catch(() => {
              this.$store.commit(`products/product/${mTypes.DATA_ERROR}`, true);
            });
        })
        .catch(() => {
        });
    },
    isPermitPack() {
      return this.type === 'permitPack';
    },
    isGuestParking() {
      return this.type === 'guestParking';
    },
    isConferenceParking() {
      return this.type === 'conferenceParking';
    },
    isEasyParking() {
      return this.isSuperAdmin() && this.type === 'easyParking';
    },
    createValidators() {
      this.createLandlordValidator();
    },
    getZonesByLandlord(clear = false) {
      if (clear) this.checkedZones = [];
      return this.zones.filter(item => Object.values(item.partners).some(({ id }) => id === this.selectedLandlord));
    },
    createLandlordValidator() {
      this.$validator.extend('validLandlord', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isSuperAdmin() && (typeof this.landlord !== 'undefined' && this.landlord !== null),
      });
    },
    updateConferenceUrl(name) {
      if (this.isNew || !this.isConferenceParking()) {
        this.form.conferenceUrl = this.sanitizeConferenceUrl(name);
      }
    },
    sanitizeConferenceUrl(url) {
      return url.replace(/[^A-Za-z0-9 -]/g, '')
        .trim()
        .replace(/( )+/g, '-')
        .replace(/(-)+/g, '-');
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~styles/variables.scss';

.virtual {
  width: 2600px;
}

.pack__name input {
  font-family: 'Geogrotesque Md', sans-serif !important;
}

.form__fieldset {
  min-width: 0;

  .form__field {

    >.form__label__long {
      padding-left: 46px;

      >.form__fieldset__custom-checkbox {
        margin-top: 50px;
        margin-left: -46px;
      }

    }

  }

}

.radiobuttons {
  margin-bottom: 15px;
}

.pack-commission {

  .details-table__col:nth-child(2) {
    width: 141px;

    @media screen and (max-width: 1370px) {
      display: inline-block;
      width: calc(100% - 5px - 100px);
    }

  }

  .details-table__col:last-child {

    @media screen and (max-width: 1370px) {
      width: 100px;
      display: inline-block;
    }

  }

}

.el-select.short {
  width: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/products/AddEditProductView.vue